import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Buildings, Plus, PencilSimple, Trash, ArrowUpRight, Clock, UploadSimple, MagnifyingGlass, CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import ImportInvestorFirmsDialog from "@/components/admin/ImportInvestorFirmsDialog";

const emptyForm = { name: "", domains: "", website: "", notes: "" };
const PAGE_SIZE = 25;

export default function AdminInvestorFirms() {
  const nav = useNavigate();
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/investor-firms", { params: { status: "approved" } });
      setFirms(r.data);
      const [p, i] = await Promise.all([
        api.get("/admin/investor-firms", { params: { status: "pending" } }),
        api.get("/admin/investor-applications"),
      ]);
      setPendingCount(p.data.length + i.data.length);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Reset back to page 1 whenever the search or dataset changes so users don't
  // land on an empty page.
  useEffect(() => { setPage(1); }, [search, firms.length]);

  // Search (name / domains / notes / website) + sort A→Z by name.
  const filteredFirms = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? firms.filter((f) => {
          if ((f.name || "").toLowerCase().includes(q)) return true;
          if ((f.notes || "").toLowerCase().includes(q)) return true;
          if ((f.website || "").toLowerCase().includes(q)) return true;
          return (f.domains || []).some((d) => (d || "").toLowerCase().includes(q));
        })
      : firms;
    return [...base].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
  }, [firms, search]);

  const totalPages = Math.max(1, Math.ceil(filteredFirms.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedFirms = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredFirms.slice(start, start + PAGE_SIZE);
  }, [filteredFirms, currentPage]);

  const rangeStart = filteredFirms.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredFirms.length);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (firm) => {
    setEditing(firm);
    setForm({
      name: firm.name || "",
      domains: (firm.domains || []).join(", "),
      website: firm.website || "",
      notes: firm.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const domains = form.domains.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean);
    if (!form.name.trim() || domains.length === 0) {
      toast.error("Name and at least one domain are required");
      return;
    }
    try {
      const payload = { name: form.name.trim(), domains, website: form.website || null, notes: form.notes || null };
      if (editing) {
        await api.patch(`/admin/investor-firms/${editing.id}`, payload);
        toast.success("Firm updated");
      } else {
        await api.post("/admin/investor-firms", payload);
        toast.success("Firm added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (firm) => {
    if (!window.confirm(`Remove ${firm.name} from the approved directory?`)) return;
    try {
      await api.delete(`/admin/investor-firms/${firm.id}`);
      toast.success("Firm removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Investor firms</h1>
          <p className="mt-2 text-muted-foreground text-sm max-w-xl">
            Manage the directory of approved VC firms. Investors sign up by choosing their firm from this list —
            only email domains listed here will be able to complete registration.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Button
              variant="outline"
              onClick={() => nav("/admin/pending-approvals")}
              data-testid="pending-approvals-cta"
              className="border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
            >
              <Clock size={16} className="mr-2" />
              {pendingCount} pending {pendingCount === 1 ? "approval" : "approvals"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="import-firms-btn"
            className="border-border"
          >
            <UploadSimple size={16} className="mr-2" /> Import
          </Button>
          <Button onClick={openNew} data-testid="add-firm-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            <Plus size={16} className="mr-2" /> Add firm
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : firms.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">
          <Buildings size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-display font-bold text-foreground text-lg">No approved firms yet</div>
          <div className="mt-2 text-sm">Add your first VC firm to open up investor self-registration.</div>
        </div>
      ) : (
        <>
          {/* Search / result summary */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by firm name, domain, website or notes…"
                className="pl-9 pr-9"
                data-testid="firms-search-input"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                  title="Clear search"
                  data-testid="firms-search-clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground num-display" data-testid="firms-result-count">
              {filteredFirms.length === firms.length
                ? `${firms.length} ${firms.length === 1 ? "firm" : "firms"} · sorted A→Z`
                : `${filteredFirms.length} of ${firms.length} match \u201C${search}\u201D`}
            </div>
          </div>

          {filteredFirms.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="firms-no-results">
              <MagnifyingGlass size={24} className="mx-auto mb-3 opacity-40" />
              <div className="font-display font-bold text-foreground text-base">No firms match &ldquo;{search}&rdquo;</div>
              <div className="mt-1 text-sm">Try a different name or domain.</div>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="firms-table">
              <div className="grid grid-cols-[1fr_1.4fr_1fr_140px_120px] px-5 py-3 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground border-b border-border">
                <div>Firm</div>
                <div>Allowed domains</div>
                <div>Website</div>
                <div>Added</div>
                <div className="text-right">Actions</div>
              </div>
              {pagedFirms.map((f) => (
                <div
                  key={f.id}
                  className="grid grid-cols-[1fr_1.4fr_1fr_140px_120px] px-5 py-4 border-b border-border last:border-b-0 items-center hover:bg-secondary/25 transition-colors"
                  data-testid={`firm-row-${f.id}`}
                >
                  <div>
                    <div className="font-medium text-sm">{f.name}</div>
                    {f.notes && <div className="text-xs text-muted-foreground truncate mt-0.5">{f.notes}</div>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(f.domains || []).map((d) => (
                      <span key={d} className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-[11px] font-mono">
                        @{d}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs">
                    {f.website ? (
                      <a href={f.website} target="_blank" rel="noreferrer" className="text-emerald-700 dark:text-emerald-400 hover:underline flex items-center gap-1">
                        {f.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                        <ArrowUpRight size={11} />
                      </a>
                    ) : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="text-xs text-muted-foreground num-display">{formatDate(f.created_at)}</div>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEdit(f)}
                      data-testid={`edit-firm-${f.id}`}
                      className="h-8 w-8 rounded grid place-items-center hover:bg-secondary text-muted-foreground hover:text-foreground"
                      title="Edit"
                    ><PencilSimple size={14} /></button>
                    <button
                      onClick={() => del(f)}
                      data-testid={`delete-firm-${f.id}`}
                      className="h-8 w-8 rounded grid place-items-center hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"
                      title="Delete"
                    ><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination controls */}
          {filteredFirms.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-xs" data-testid="firms-pagination">
              <div className="text-muted-foreground num-display">
                Showing <span className="font-semibold text-foreground">{rangeStart}–{rangeEnd}</span> of{" "}
                <span className="font-semibold text-foreground">{filteredFirms.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="firms-page-prev"
                  className="h-8 px-3 rounded border border-border flex items-center gap-1 text-xs font-medium hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretLeft size={12} /> Prev
                </button>
                <span className="px-3 num-display text-muted-foreground" data-testid="firms-page-indicator">
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="firms-page-next"
                  className="h-8 px-3 rounded border border-border flex items-center gap-1 text-xs font-medium hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <CaretRight size={12} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit firm" : "Add investor firm"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Firm name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bessemer Venture Partners" className="mt-1" data-testid="firm-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Allowed email domains</Label>
              <Input value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} placeholder="bessemer.com, bvp.com" className="mt-1 font-mono" data-testid="firm-domains-input" />
              <div className="text-xs text-muted-foreground mt-1">Comma-separated. An investor&apos;s email must end with @&lt;domain&gt; to register.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Notes (internal)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="save-firm-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
              {editing ? "Save changes" : "Add firm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportInvestorFirmsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={load}
      />
    </div>
  );
}
